import { ZjProAbortAddShift } from './types/ZjProAbortAddShift';
import { ZjProCompleteAddShift } from './types/ZjProCompleteAddShift';
import { ZjProElementTapped } from './types/ZjProElementTapped';
import { ZjProInitAddShift } from './types/ZjProInitAddShift';
import { ZjProInitLogin } from './types/ZjProInitLogin';
import { ZjProScreenViewed } from './types/ZjProScreenViewed';
import { track } from './analytics';

export const sendElementTapped = (props: ZjProElementTapped['properties']) =>
  void track('zj_pro_element_tapped', props);

export const sendScreenViewed = (props: ZjProScreenViewed['properties']) =>
  void track('zj_pro_screen_viewed', props);

export const sendInitAddShift = (props: ZjProInitAddShift['properties']) =>
  void track('zj_pro_init_add_shift', props);

export const sendCompleteShift = (props: ZjProCompleteAddShift['properties']) =>
  void track('zj_pro_complete_add_shift', props);

export const sendAbortCompleteShift = (
  props: ZjProAbortAddShift['properties']
) => void track('zj_pro_abort_add_shift', props);

export const sendInitLogin = (props: ZjProInitLogin['properties']) =>
  void track('zj_pro_init_login', props);

/**
 * "PRO ATS" experiment 3 (zenjob/pro-core-service#1335): the tracker was shown to a talent. `variant` is the
 * PRO_ATS arm they are in, `applicationCount` how many applications the list held. Hand-typed because the
 * event is not in the Segment tracking plan the generated types come from yet.
 */
export interface AtsTrackerViewed {
  variant: string;
  applicationCount: number;
  /** How many of them moved today (Berlin day), the ones the list puts first. */
  movedTodayCount: number;
  /** Whether the applications block was open when the page came up, by default or by the talent's choice. */
  blockOpen: boolean;
}

export const sendAtsTrackerViewed = (props: AtsTrackerViewed) =>
  void track('ats_tracker_viewed', props);

/**
 * "PRO ATS" experiment 3 (zenjob/pro-core-service#1335): a talent withdrew one of their own applications. `stepKey`
 * is the step they withdrew from, `reasonLength` how much they wrote (the words themselves stay between them and the
 * operator), `variant` the PRO_ATS arm. Hand-typed for the same reason as the event above.
 */
export interface AtsApplicationWithdrawn {
  variant: string;
  stepKey: string;
  reasonLength: number;
}

export const sendAtsApplicationWithdrawn = (props: AtsApplicationWithdrawn) =>
  void track('ats_application_withdrawn', props);

/**
 * "PRO ATS" experiment 3 (zenjob/pro-core-service#1335): a talent followed the link in a status email from pro-core.
 * Both arms send it, which is what makes the arms comparable: the link is the same for everybody and `destination`
 * says where it led, the application in the tracker arm and the CV builder in control. Hand-typed for the same
 * reason as the two events above.
 */
export interface AtsStatusEmailLinkClicked {
  variant: string;
  destination: string;
  applicationRefId: string;
}

export const sendAtsStatusEmailLinkClicked = (
  props: AtsStatusEmailLinkClicked
) => void track('ats_status_email_link_clicked', props);

/**
 * A destination of the bottom navigation was tapped. `destination` is the stable name of the target (`weeks`,
 * `applications`, `account`), not its label, so a relabelling does not split the series. Hand-typed like the ATS
 * events: not in the Segment tracking plan the generated types come from yet.
 */
export interface NavDestinationTapped {
  destination: 'weeks' | 'applications' | 'account';
}

export const sendNavDestinationTapped = (props: NavDestinationTapped) =>
  void track('nav_destination_tapped', props);

/**
 * Phase 2 (zenjob/pro-talent-app#465): the job feed was shown. `filterCount` is how many filters were set, `resultCount`
 * how many listings the whole search holds, `hasPoint` whether a saved point narrowed it. Hand-typed like the events above.
 */
export interface AtsJobFeedViewed {
  filterCount: number;
  resultCount: number;
  hasPoint: boolean;
}

export const sendAtsJobFeedViewed = (props: AtsJobFeedViewed) =>
  void track('ats_job_feed_viewed', props);

/** A listing was opened from the feed: which one, at what position in the list, and whether the talent had already applied. */
export interface AtsJobListingOpened {
  jobListingRefId: string;
  position: number;
  hasApplied: boolean;
}

export const sendAtsJobListingOpened = (props: AtsJobListingOpened) =>
  void track('ats_job_listing_opened', props);

/** The talent applied a filter change; the honest test of whether the filters were worth building. */
export interface AtsJobFeedFiltered {
  filterCount: number;
  hasPoint: boolean;
}

export const sendAtsJobFeedFiltered = (props: AtsJobFeedFiltered) =>
  void track('ats_job_feed_filtered', props);

/**
 * Phase 2 (zenjob/pro-talent-app#466): the talent applied from the app. `created` false is the sibling-wording case,
 * an application they already had, which is the number that says whether variants confuse people.
 */
export interface AtsJobApplied {
  jobListingRefId: string;
  applicationRefId: string;
  created: boolean;
}

export const sendAtsJobApplied = (props: AtsJobApplied) =>
  void track('ats_job_applied', props);

/** The apply was refused or failed; `errorCode` is pro-core's code or the app's unnamed one. */
export interface AtsJobApplyFailed {
  jobListingRefId: string;
  errorCode: string;
}

export const sendAtsJobApplyFailed = (props: AtsJobApplyFailed) =>
  void track('ats_job_apply_failed', props);
